import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

export type MultiComboboxOption = {
  readonly value: string;
  readonly label: string;
};

export type MultiComboboxGroup = {
  readonly label: string;
  readonly options: ReadonlyArray<MultiComboboxOption>;
};

type MultiComboboxProps = {
  readonly id: string;
  readonly options: ReadonlyArray<MultiComboboxOption>;
  // When present, the listbox renders headed groups instead of the flat
  // `options` list. `options` must still carry every option — it stays the
  // lookup for the selected chips' labels (IMM-485).
  readonly groups?: ReadonlyArray<MultiComboboxGroup> | undefined;
  readonly value: ReadonlyArray<string>;
  readonly onChange: (value: ReadonlyArray<string>) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly noResultsLabel: string;
  readonly removeLabel: (label: string) => string;
  readonly invalid?: boolean;
  readonly 'aria-describedby'?: string | undefined;
};

// The listbox is portalled to <body> and positioned `fixed` off the field's
// viewport rect, so it anchors below by default and flips above when it would
// overflow the viewport bottom. Mirrors the `reposition()` idiom in
// `components/menu/actions-menu.tsx`.
type ListboxPosition = {
  readonly left: number;
  readonly width: number;
  readonly maxHeight: number;
} & ({ readonly top: number } | { readonly bottom: number });

function positionStyle(position: ListboxPosition | null): CSSProperties {
  if (position === null) return { visibility: 'hidden' };

  const base: CSSProperties = { left: position.left, width: position.width, maxHeight: position.maxHeight };

  return 'top' in position ? { ...base, top: position.top } : { ...base, bottom: position.bottom };
}

function matches(label: string, query: string): boolean {
  return label.toLocaleLowerCase().includes(query);
}

// Generic static-option multi-select combobox: selected values render as chips
// *inside* a tokenized field, and a dropdown offers the remaining options.
// Selection is restricted to the option list — there is no free-text entry.
// Generalised from the contact-bound OwnersMultiSelect (IMM-143) pattern.
export function MultiCombobox(props: MultiComboboxProps) {
  const {
    id,
    options,
    groups,
    value,
    onChange,
    disabled = false,
    placeholder,
    noResultsLabel,
    removeLabel,
    invalid = false,
  } = props;
  const ariaDescribedBy = props['aria-describedby'];

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<ListboxPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards the input's `onFocus`: `add()` refocuses the input programmatically,
  // and that must not re-open the list it just closed. IMM-519.
  const reopenOnFocusRef = useRef(true);

  const labelByValue = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);
  const normalizedQuery = query.trim().toLocaleLowerCase();

  const filtered = useMemo(
    () => options.filter((o) => !value.includes(o.value) && matches(o.label, normalizedQuery)),
    [options, value, normalizedQuery],
  );

  // A group whose options all get filtered out renders neither its heading nor a
  // gap — hence the empty groups are dropped before render.
  const filteredGroups = useMemo(() => {
    if (groups === undefined) return null;

    return groups
      .map((group) => ({
        label: group.label,
        options: group.options.filter((o) => !value.includes(o.value) && matches(o.label, normalizedQuery)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, value, normalizedQuery]);

  const hasResults = filteredGroups !== null ? filteredGroups.length > 0 : filtered.length > 0;

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);

      return;
    }

    const reposition = () => {
      const field = containerRef.current;

      if (field === null) return;

      const rect = field.getBoundingClientRect();

      // Close rather than trail the field off-screen once it leaves the viewport
      // — the same rule IMM-471 settles for ActionsMenu, kept in sync here.
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);

        return;
      }

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const listHeight = listboxRef.current?.getBoundingClientRect().height ?? 0;
      const flip = listHeight > spaceBelow && spaceAbove > spaceBelow;
      const available = (flip ? spaceAbove : spaceBelow) - 8;
      const maxHeight = Math.max(96, Math.min(256, available));

      setPosition(
        flip
          ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width, maxHeight }
          : { top: rect.bottom + 4, left: rect.left, width: rect.width, maxHeight },
      );
    };

    reposition();
    window.addEventListener('resize', reposition);
    // Capture phase catches scrolls in any ancestor (the modal body), not just window.
    window.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inField = containerRef.current?.contains(target) === true;
      // Once portalled, the listbox is no longer a descendant of the field, so a
      // click inside it must be treated as inside or every option click closes
      // the list before it registers (IMM-485).
      const inListbox = listboxRef.current?.contains(target) === true;

      if (!inField && !inListbox) setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setOpen(false);
      // The modal's own Escape handler is a bubble-phase document listener
      // (packages/ui-kit/src/modal.tsx). Without this the one keypress would
      // close the listbox AND the dialog. IMM-519.
      event.stopPropagation();
    };

    // Capture phase, not bubble: a container may legitimately stop propagation
    // on its own subtree — `Modal`'s panel does, so a click inside the dialog
    // does not read as a backdrop click — which would leave a bubble-phase
    // listener here dead for every click the user can make inside a dialog.
    // Capture runs from `document` downward, before any ancestor's bubble-phase
    // `stopPropagation` and before the modal's own Escape handler (BL-023).
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      // The same `true` must be passed here — capture and bubble registrations
      // are distinct, and omitting it leaks the listener on every open/close.
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const add = (optionValue: string) => {
    onChange([...value, optionValue]);
    setQuery('');
    // A pick closes the list so the dialog's own controls — the submit button
    // the listbox is drawn over — stay clickable. `setOpen(false)` below is what
    // actually closes it: options `preventDefault()` their mousedown, so on the
    // mouse path the input never lost focus and `focus()` fires no event at all.
    // The flag covers the keyboard path, where an option *did* take focus and
    // handing it back would otherwise re-open the list through `onFocus`. It is
    // restored immediately after, since `focus()` dispatches synchronously when
    // it dispatches at all. IMM-519.
    reopenOnFocusRef.current = false;
    inputRef.current?.focus();
    reopenOnFocusRef.current = true;
    setOpen(false);
  };

  const remove = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && query === '' && value.length > 0) {
      remove(value[value.length - 1] as string);
    }
  };

  const renderOption = (option: MultiComboboxOption) => (
    <button
      key={option.value}
      type="button"
      role="option"
      aria-selected={false}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => add(option.value)}
      className="flex w-full items-start rounded-md px-3 py-2 text-left text-sm text-text-primary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row"
    >
      {option.label}
    </button>
  );

  return (
    <div ref={containerRef} className="relative">
      <div
        onMouseDown={(event) => {
          // A disabled picker must not open. Before IMM-519 this fell out of the
          // input being unfocusable, so the `onFocus` that opened the list never
          // fired; opening from the field explicitly has to say it.
          if (disabled) return;
          // A chip's remove control lives inside the field; clicking it only
          // removes a value and must leave the open state alone. Bounded to the
          // field: an unbounded `closest` would also match an ancestor button
          // this ever gets nested under and silently dead-end the picker.
          const button = (event.target as HTMLElement).closest('button');

          if (button !== null && event.currentTarget.contains(button)) return;

          if (event.target === event.currentTarget) inputRef.current?.focus();
          // The input keeps focus after a pick (see `add`), so `onFocus` cannot
          // be the only way back in — without this, clicking the field again
          // would silently do nothing and the picker would be re-openable by
          // typing alone. The capture-phase document listener above runs first
          // and correctly treats this as a click inside the field. IMM-519.
          setOpen(true);
        }}
        className={[
          'flex min-h-control-md w-full flex-wrap items-center gap-1.5 rounded-md border bg-surface px-2 py-1',
          'transition-colors duration-micro ease-out focus-within:border-interactive focus-within:shadow-focus',
          invalid ? 'border-danger-500' : 'border-border',
          disabled ? 'opacity-60' : '',
        ]
          .filter((c) => c !== '')
          .join(' ')}
      >
        {value.map((optionValue) => {
          const label = labelByValue.get(optionValue) ?? optionValue;

          return (
            <span
              key={optionValue}
              className="inline-flex items-center gap-1 rounded bg-neutral-100 py-0.5 pl-2 pr-1 text-sm text-text-primary"
            >
              <span>{label}</span>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => remove(optionValue)}
                disabled={disabled}
                aria-label={removeLabel(label)}
                className="inline-flex h-4 w-4 items-center justify-center rounded text-text-tertiary hover:bg-hover-bg hover:text-text-primary disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-focus"
              >
                ×
              </button>
            </span>
          );
        })}

        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          value={query}
          placeholder={value.length === 0 ? placeholder : ''}
          onFocus={() => {
            if (reopenOnFocusRef.current) setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);

            if (!open) setOpen(true);
          }}
          onKeyDown={onInputKeyDown}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          autoComplete="off"
          className="min-w-[8rem] flex-1 bg-transparent py-0.5 text-base text-text-primary outline-none placeholder:text-text-placeholder disabled:cursor-not-allowed"
        />
      </div>

      {open &&
        createPortal(
          <div
            ref={listboxRef}
            id={`${id}-listbox`}
            role="listbox"
            // Rendered before it is measured so `reposition()` can read its
            // height; kept invisible (not unmounted) until placed to avoid a
            // one-frame flash at the wrong spot.
            style={positionStyle(position)}
            className="fixed z-[70] overflow-auto rounded-md border border-border bg-surface shadow-lg"
          >
            {!hasResults ? (
              <p className="px-3 py-2 text-sm text-text-tertiary">{noResultsLabel}</p>
            ) : filteredGroups !== null ? (
              filteredGroups.map((group) => (
                <div key={group.label}>
                  <div
                    role="presentation"
                    className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-text-tertiary"
                  >
                    {group.label}
                  </div>
                  {group.options.map(renderOption)}
                </div>
              ))
            ) : (
              filtered.map(renderOption)
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
